import type { ColumnDef } from '@tanstack/react-table';
import { SubmissionStatus, WaitingOn, type IsoDate } from '@clawhire/shared';
import { EMPTY, truncate } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { EnumBadge } from '@/components/data/EnumBadge';
import { RateText } from '@/components/data/RateText';
import { ActualMarginCell, DateCell } from '@/pages/submissions/cells';
import { candidateLine, overdueDays } from '@/pages/submissions/helpers';
import type { SubmissionListItem } from '@/pages/submissions/types';

/** Column ids, also the vocabulary of saved views' `columns`. */
export const SUBMISSION_COLUMN_IDS = [
  'title',
  'status',
  'waitingOn',
  'nextStep',
  'followUpOn',
  'feedbackDue',
  'candidateRateEur',
  'clientRateEur',
  'actualMarginPct',
  'needsChase',
] as const;

export type SubmissionColumnId = (typeof SUBMISSION_COLUMN_IDS)[number];

/** Table columns for the submissions list. `today` drives the overdue highlighting. */
// eslint-disable-next-line react-refresh/only-export-components -- column factory, not a component
export function submissionColumns(today: IsoDate): ColumnDef<SubmissionListItem, unknown>[] {
  return [
    {
      id: 'title',
      accessorFn: (s) => s.computed.title,
      header: 'Submission',
      enableHiding: false,
      cell: ({ row }) => (
        <div className="min-w-[14rem]">
          <div className="font-medium">{row.original.computed.title}</div>
          <div className="text-xs text-muted-foreground">{candidateLine(row.original.candidate)}</div>
        </div>
      ),
    },
    {
      id: 'status',
      accessorFn: (s) => s.status,
      header: 'Status',
      cell: ({ row }) => <EnumBadge def={SubmissionStatus} value={row.original.status} />,
    },
    {
      id: 'waitingOn',
      accessorFn: (s) => s.waitingOn ?? undefined,
      header: 'Waiting on',
      sortUndefined: 'last',
      cell: ({ row }) =>
        row.original.waitingOn ? (
          <EnumBadge def={WaitingOn} value={row.original.waitingOn} size="sm" />
        ) : (
          <span className="text-muted-foreground">{EMPTY}</span>
        ),
    },
    {
      id: 'nextStep',
      accessorFn: (s) => s.nextStep ?? undefined,
      header: 'Next step',
      sortUndefined: 'last',
      cell: ({ row }) =>
        row.original.nextStep ? (
          <span title={row.original.nextStep}>{truncate(row.original.nextStep, 60)}</span>
        ) : (
          <span className="text-muted-foreground">{EMPTY}</span>
        ),
    },
    {
      id: 'followUpOn',
      accessorFn: (s) => s.followUpOn ?? undefined,
      header: 'Follow-up on',
      sortUndefined: 'last',
      cell: ({ row }) => (
        <DateCell
          value={row.original.followUpOn}
          overdue={
            row.original.computed.isTerminal ? null : overdueDays(row.original.followUpOn, today, true)
          }
        />
      ),
    },
    {
      id: 'feedbackDue',
      accessorFn: (s) => s.feedbackDue ?? undefined,
      header: 'Feedback due',
      sortUndefined: 'last',
      cell: ({ row }) => (
        <DateCell
          value={row.original.feedbackDue}
          overdue={
            row.original.computed.isTerminal ? null : overdueDays(row.original.feedbackDue, today, false)
          }
        />
      ),
    },
    {
      id: 'candidateRateEur',
      accessorFn: (s) => s.candidateRateEur ?? undefined,
      header: 'Candidate rate',
      sortUndefined: 'last',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <RateText rate={row.original.candidateRateEur} unit={row.original.candidateRateUnit} />
      ),
    },
    {
      id: 'clientRateEur',
      accessorFn: (s) => s.rates.offerInUse ?? undefined,
      header: 'Client offer',
      sortUndefined: 'last',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <span className="whitespace-nowrap">
          <RateText rate={row.original.rates.offerInUse} />
          {row.original.clientRateEur === null && row.original.rates.offerInUse !== null ? (
            <span className="ml-1 text-xs text-muted-foreground">(suggested)</span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'actualMarginPct',
      accessorFn: (s) => s.rates.actualMarginPct ?? undefined,
      header: 'Actual margin',
      sortUndefined: 'last',
      meta: { align: 'right' },
      cell: ({ row }) => <ActualMarginCell item={row.original} />,
    },
    {
      id: 'needsChase',
      accessorFn: (s) => (s.computed.needsChase ? 1 : 0),
      header: 'Chase',
      meta: { align: 'center' },
      cell: ({ row }) =>
        row.original.computed.needsChase ? (
          <Badge variant="destructive" className="px-1.5">
            Chase
          </Badge>
        ) : null,
    },
  ];
}
